/**
 * Casa Encanto · comportamentos do visual de varejo sobre o Horizon.
 * - Botão Comprar leva direto ao carrinho
 * - Blocos que acompanham a troca de variante (<ce-variant-sync>)
 * - Contagem regressiva de oferta (<ce-countdown>)
 * - Carrossel de categorias (<ce-carousel>)
 * - Abas da descrição (<ce-tabs>)
 * - Frete por CEP no carrinho (<ce-shipping>)
 * - Vistos recentemente (<ce-recent>)
 */
import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';

const config = window.CasaEncanto || {};

/* ---------- Comprar → carrinho ---------- */
if (config.redirectToCart) {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (event.action && event.action !== 'add') return;
    // Só na página do produto: ignora quick add, cards e diálogos
    if (target.closest('dialog, product-card, .product-card, quick-add-component')) return;
    if (!target.closest('.product-information, [data-ce-buy-redirect]')) return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        window.location.href = config.cartUrl || '/cart';
      })
      .catch(() => {});
  });
}

/* ---------- Sincroniza blocos Liquid quando a variante muda ---------- */
class CeVariantSync extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.onSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.onSelect);
  }

  onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;
        const fresh = html.querySelector(`ce-variant-sync[data-ce-id="${this.dataset.ceId}"]`);
        if (fresh) morph(this, fresh, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ce-variant-sync]', error);
      });
  };
}

if (!customElements.get('ce-variant-sync')) customElements.define('ce-variant-sync', CeVariantSync);

/* ---------- Contagem regressiva ---------- */
class CeCountdown extends HTMLElement {
  connectedCallback() {
    this.units = {
      d: this.querySelector('[data-unit="d"]'),
      h: this.querySelector('[data-unit="h"]'),
      m: this.querySelector('[data-unit="m"]'),
      s: this.querySelector('[data-unit="s"]'),
    };
    this.tick();
    this.timer = window.setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  get end() {
    const mode = this.dataset.mode || 'date';
    const now = new Date();
    if (mode === 'daily') {
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return end.getTime();
    }
    const seconds = Number(this.dataset.end);
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
  }

  tick() {
    let diff = Math.max(0, this.end - Date.now());
    if (diff === 0) {
      const box = this.closest('.ce-offer');
      box?.setAttribute('data-expired', '');
      if (this.dataset.hideExpired === 'true' && box) box.hidden = true;
      window.clearInterval(this.timer);
    }
    const d = Math.floor(diff / 86400000);
    diff -= d * 86400000;
    const h = Math.floor(diff / 3600000);
    diff -= h * 3600000;
    const m = Math.floor(diff / 60000);
    diff -= m * 60000;
    const s = Math.floor(diff / 1000);
    const pad = (n) => String(n).padStart(2, '0');
    if (this.units.d) this.units.d.textContent = pad(d);
    if (this.units.h) this.units.h.textContent = pad(h);
    if (this.units.m) this.units.m.textContent = pad(m);
    if (this.units.s) this.units.s.textContent = pad(s);
  }
}

if (!customElements.get('ce-countdown')) customElements.define('ce-countdown', CeCountdown);

/* ---------- Carrossel simples com setas ---------- */
class CeCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ce-track]');
    this.prev = this.querySelector('[data-ce-prev]');
    this.next = this.querySelector('[data-ce-next]');
    if (!this.track) return;
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.track.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.update, { passive: true });
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.update);
  }

  go(direction) {
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    this.track.scrollBy({ left: direction * rtl * this.track.clientWidth * 0.9, behavior: 'smooth' });
  }

  update = () => {
    if (!this.track) return;
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    const pos = Math.abs(this.track.scrollLeft);
    if (this.prev) this.prev.disabled = pos <= 2;
    if (this.next) this.next.disabled = pos >= max;
  };
}

if (!customElements.get('ce-carousel')) customElements.define('ce-carousel', CeCarousel);

/* ---------- Abas ---------- */
class CeTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab));
      tab.addEventListener('keydown', (event) => {
        const index = this.tabs.indexOf(tab);
        if (event.key === 'ArrowRight') this.select(this.tabs[(index + 1) % this.tabs.length], true);
        if (event.key === 'ArrowLeft') this.select(this.tabs[(index - 1 + this.tabs.length) % this.tabs.length], true);
      });
    });
  }

  select(tab, focus = false) {
    this.tabs.forEach((other) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const panel = this.querySelector(`#${other.getAttribute('aria-controls')}`);
      if (panel) panel.hidden = !selected;
    });
    if (focus) tab.focus();
  }
}

if (!customElements.get('ce-tabs')) customElements.define('ce-tabs', CeTabs);

/* ---------- Frete por CEP (carrinho) ---------- */
class CeShipping extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input');
    this.results = this.querySelector('[data-ce-results]');
    this.message = this.querySelector('[data-ce-message]');
    if (!this.form || !this.input) return;

    const saved = this.readSaved();
    if (saved) this.input.value = saved;

    this.input.addEventListener('input', () => {
      const digits = this.input.value.replace(/\D/g, '').slice(0, 8);
      this.input.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.calculate();
    });
  }

  readSaved() {
    try {
      return localStorage.getItem('ce-cep') || '';
    } catch {
      return '';
    }
  }

  say(text) {
    if (this.message) this.message.textContent = text;
  }

  async calculate() {
    const cep = this.input.value.replace(/\D/g, '');
    this.results.innerHTML = '';
    if (cep.length !== 8) {
      this.say('Digite um CEP válido com 8 números.');
      return;
    }
    try {
      localStorage.setItem('ce-cep', this.input.value);
    } catch {}
    this.say('Calculando...');

    let province = '';
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (data.erro) {
        this.say('CEP não encontrado.');
        return;
      }
      province = data.uf || '';
    } catch {
      // Sem a UF ainda tentamos calcular só com o CEP
    }

    const params = new URLSearchParams({
      'shipping_address[zip]': `${cep.slice(0, 5)}-${cep.slice(5)}`,
      'shipping_address[country]': 'BR',
      'shipping_address[province]': province,
    });

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/shipping_rates.json?${params}`);
      const data = await response.json();
      const rates = data.shipping_rates || [];
      if (!response.ok || rates.length === 0) {
        this.say('Não encontramos opções de frete para esse CEP. O valor final aparece no checkout.');
        return;
      }
      this.say('');
      this.results.innerHTML = rates
        .map((rate) => {
          const price = Number(rate.price);
          const value = price === 0 ? 'Grátis' : this.money(price * 100);
          const days =
            rate.delivery_days && rate.delivery_days.length
              ? ` · ${rate.delivery_days[0]}${rate.delivery_days[1] && rate.delivery_days[1] !== rate.delivery_days[0] ? `–${rate.delivery_days[1]}` : ''} dias úteis`
              : '';
          return `<li><span>${this.escape(rate.name)}${days}</span><b>${value}</b></li>`;
        })
        .join('');
    } catch {
      this.say('Não foi possível calcular agora. O valor final aparece no checkout.');
    }
  }

  money(cents) {
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  escape(text) {
    const div = document.createElement('div');
    div.textContent = String(text ?? '');
    return div.innerHTML;
  }
}

if (!customElements.get('ce-shipping')) customElements.define('ce-shipping', CeShipping);

/* ---------- Vistos recentemente ---------- */
const RECENT_KEY = 'ce-recent';

function readRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch {
    return [];
  }
}

// Registra o produto atual (dados vêm de <script type="application/json" data-ce-recent-product>)
const currentProduct = document.querySelector('script[data-ce-recent-product]');
if (currentProduct) {
  try {
    const product = JSON.parse(currentProduct.textContent || '{}');
    if (product.handle) {
      const list = readRecent().filter((item) => item.handle !== product.handle);
      list.unshift(product);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 12)));
    }
  } catch {}
}

class CeRecent extends HTMLElement {
  connectedCallback() {
    const skip = this.dataset.skip || '';
    const limit = Number(this.dataset.limit) || 6;
    const items = readRecent()
      .filter((item) => item.handle && item.handle !== skip)
      .slice(0, limit);
    const grid = this.querySelector('[data-ce-recent-grid]');
    if (!grid || items.length === 0) {
      if (!window.Shopify?.designMode) this.closest('.shopify-section')?.setAttribute('hidden', '');
      return;
    }
    const escape = (text) => {
      const div = document.createElement('div');
      div.textContent = String(text ?? '');
      return div.innerHTML;
    };
    grid.innerHTML = items
      .map(
        (item) => `
        <a class="ce-recent__item ce-card" href="${encodeURI(item.url)}">
          ${item.image ? `<img src="${encodeURI(item.image)}" alt="${escape(item.title)}" loading="lazy" width="300" height="300">` : ''}
          <span class="ce-recent__title">${escape(item.title)}</span>
          <span class="ce-recent__price">${escape(item.price)}</span>
        </a>`
      )
      .join('');
    this.hidden = false;
  }
}

if (!customElements.get('ce-recent')) customElements.define('ce-recent', CeRecent);
